import {t} from "../../digitization/i18n/index.js";

// OGC API - Features (https://ogcapi.ogc.org/features/) som GIS-kilde, ved siden af WFS (LERWFS.js): henter vektordata som GeoJSON
// inden for en bbox, uden en fil at gå via. Resultatet er en almindelig GeoJSON FeatureCollection, der går gennem præcis den samme
// vej som en GeoJSON-fil (featuresFromGeoJSON i GenericVectorParser.js + frontendens reprojektion) - der er ingen separat
// visning.
//
// Protokollen (Part 1 Core + Part 2 CRS, hvor serveren har den):
//   {service}/collections                    -> lagene ("samlingerne") med id, titel og de koordinatsystemer, de kan leveres i
//   {service}/collections/{id}/items?bbox=.. -> objekterne som GeoJSON, 10 ad gangen som standard -> `limit`, og derefter
//                                               links med rel="next" til de næste sider
//
// Koordinatsystem: kan serveren levere i punktskyens eget EPSG (Part 2: `crs`-listen på samlingen), bedes der direkte om det
// (crs= og bbox-crs=), så intet skal omregnes. Ellers hentes standardsystemet CRS84 (lon/lat), bboxen sendes i lon/lat, og
// kalderen omregner til punktskyens system (gis.vue gør det som for enhver anden GeoJSON). `crsKind` i svaret siger, hvilket.
//
// Adressen kan indeholde egne parametre (fx ?token=... eller ?apikey=...): de bevares ved ALLE kald, også på sidelinks, hvor
// serveren selv har glemt dem. Brugernavn/adgangskode sendes som Basic-header (ikke i adressen), som for WFS.
//
// Sikkerhedsnet som for WFS: tidsgrænse pr. kald, størrelsesgrænse pr. svar, og et loft over antal sider/objekter, så en server,
// der ignorerer bbox'en (og dermed ville sende hele sit datasæt), ikke tager fanen med sig.

const OGC_TIMEOUT_MS = 20000;
const OGC_MAX_RESPONSE_BYTES = 25 * 1024 * 1024;
const DEFAULT_PAGE_LIMIT = 1000;
const DEFAULT_MAX_FEATURES = 20000;
const DEFAULT_MAX_PAGES = 30;

// OGC-forespørgselsparametre, der ikke hører hjemme i en gemt adresse (hvis nogen indsætter en items-adresse med dem) - alt andet
// i adressens søgestreng (token, nøgle ...) bevares.
const OGC_OWN_PARAMS = ["f", "bbox", "bbox-crs", "crs", "limit", "offset", "startindex", "datetime", "next", "token_page"];

// Lag- og landingssider (almindelig JSON) og objekter (GeoJSON) har hver sin medietype; nogle servere (PDOK) svarer 404/406 på
// geo+json for en side, der ikke er GeoJSON - så sider beder om application/json, objekter om geo+json med json som reserve.
const ACCEPT_JSON = "application/json";
const ACCEPT_GEOJSON = "application/geo+json, application/json;q=0.9";

// ---- koordinatsystem-identifikatorer ---------------------------------------------------------------------------------------

// http://www.opengis.net/def/crs/EPSG/0/25832, urn:ogc:def:crs:EPSG::25832 og EPSG:25832 -> "EPSG:25832"
// http://www.opengis.net/def/crs/OGC/1.3/CRS84 og urn:ogc:def:crs:OGC:1.3:CRS84 -> "CRS84"; andet -> null
export function ogcCrsKey(identifier){
	if(typeof identifier !== "string") return null;
	let s = identifier.trim().replace(/^<|>$/g, "");
	let m = s.match(/\/def\/crs\/EPSG\/[^/]*\/(\d+)$/i) || s.match(/^urn:ogc:def:crs:EPSG:[^:]*:(\d+)$/i) || s.match(/^EPSG:(\d+)$/i);
	if(m) return "EPSG:" + m[1];
	if(/\/def\/crs\/OGC\/[^/]*\/CRS84$/i.test(s) || /^urn:ogc:def:crs:OGC:[^:]*:CRS84$/i.test(s) || /^CRS84$/i.test(s)) return "CRS84";
	return null;
}

// Hvilket system skal der bedes om? targetEpsg (fx 25832) direkte, hvis samlingen tilbyder det - ellers standardsystemet (lon/lat).
export function pickOgcRequestCrs(collection, targetEpsg){
	if(targetEpsg){
		let wanted = "EPSG:" + targetEpsg;
		let offered = (collection.crs || []).find(c => ogcCrsKey(c) === wanted);
		if(offered) return {crs: offered, kind: "target"};
	}
	return {crs: null, kind: "lonlat"};
}

// ---- adresser ---------------------------------------------------------------------------------------------------------------

// Tillader, at brugeren indsætter hvad som helst fra serveren: landingssiden, /collections, /collections/{id} eller
// /collections/{id}/items?limit=10&f=json. Returnerer tjenestens rod (med eget søgestreng-indhold, fx token) og evt. det lag,
// adressen pegede på.
export function normalizeOgcServiceUrl(input){
	let url = new URL(String(input || "").trim());
	let path = url.pathname.replace(/\/+$/, "");
	path = path.replace(/\/items(\/[^/]*)?$/, "");
	let collectionId = null;
	let m = path.match(/^(.*)\/collections(?:\/([^/]+))?$/);
	if(m){
		path = m[1];
		collectionId = m[2] ? decodeURIComponent(m[2]) : null;
	}
	url.pathname = path || "/";
	url.hash = "";
	for(let name of OGC_OWN_PARAMS) url.searchParams.delete(name);
	return {baseUrl: url.toString(), collectionId};
}

function buildUrl(baseUrl, path, params){
	let url = new URL(baseUrl);
	if(path) url.pathname = url.pathname.replace(/\/+$/, "") + path;
	for(let [name, value] of Object.entries(params || {})){
		if(value !== undefined && value !== null) url.searchParams.set(name, value);
	}
	return url.toString();
}

// Et sidelink (rel="next") kan mangle adressens egne parametre (token ...) - de lægges på igen, men kun når linket peger på
// samme server (en nøgle sendes ikke videre til et fremmed domæne).
function withServiceParams(href, referenceUrl, serviceUrl){
	let url = new URL(href, referenceUrl);
	let service = new URL(serviceUrl);
	if(url.origin === service.origin){
		for(let [name, value] of service.searchParams){
			if(!url.searchParams.has(name)) url.searchParams.set(name, value);
		}
	}
	return url.toString();
}

// ---- netværk ----------------------------------------------------------------------------------------------------------------

function authHeaders(credentials){
	if(!credentials || !credentials.username) return {};
	return {"Authorization": "Basic " + btoa(`${credentials.username}:${credentials.password || ""}`)};
}

async function fetchWithTimeout(url, options, timeoutMs = OGC_TIMEOUT_MS){
	let controller = new AbortController();
	let timer = setTimeout(() => controller.abort(), timeoutMs);
	try{
		return await fetch(url, {...options, signal: controller.signal});
	}catch(err){
		if(err.name === "AbortError"){
			throw new Error(t("Serveren svarede ikke inden for {0} sekunder - prøv en mindre bufferzone, eller kontrollér adressen/serverens status", Math.round(timeoutMs / 1000)));
		}
		throw err;
	}finally{
		clearTimeout(timer);
	}
}

// Serverens egen fejlbesked, hvis den giver en (OGC: {"code", "description"}; RFC 7807: {"title", "detail"}).
async function describeHttpError(response){
	let detail = "";
	try{
		let text = await response.text();
		try{
			let body = JSON.parse(text);
			detail = body.detail || body.description || body.title || body.message || "";
		}catch(e){
			detail = /<html/i.test(text) ? "" : text;
		}
	}catch(e){
		// ingen læsbar krop
	}
	detail = String(detail).replace(/\s+/g, " ").trim().slice(0, 200);
	return detail ? `: ${detail}` : "";
}

// Henter og tolker ét JSON-svar. Returnerer {body, response}.
async function getJson(url, credentials, what, accept = ACCEPT_JSON){
	let response = await fetchWithTimeout(url, {headers: {"Accept": accept, ...authHeaders(credentials)}});
	if(!response.ok && accept !== ACCEPT_JSON && [404, 406, 415].includes(response.status)){
		// serveren kender ikke geo+json som svar: prøv igen med almindelig JSON
		response = await fetchWithTimeout(url, {headers: {"Accept": ACCEPT_JSON, ...authHeaders(credentials)}});
	}
	if(!response.ok){
		throw new Error(t("{0} fejlede (HTTP {1}){2}", what, response.status, await describeHttpError(response)));
	}

	let declaredLength = Number(response.headers.get("content-length"));
	if(Number.isFinite(declaredLength) && declaredLength > OGC_MAX_RESPONSE_BYTES){
		throw new Error(t("{0}: svaret er {1} MB - for stort til at indlæse. Prøv en mindre bufferzone.", what, (declaredLength / 1048576).toFixed(1)));
	}
	let text = await response.text();
	if(text.length > OGC_MAX_RESPONSE_BYTES){
		throw new Error(t("{0}: svaret er {1} MB - for stort til at indlæse. Prøv en mindre bufferzone.", what, (text.length / 1048576).toFixed(1)));
	}

	let body;
	try{
		body = JSON.parse(text);
	}catch(e){
		throw new Error(t("{0}: svaret er ikke JSON - er adressen en OGC API - Features-tjeneste? (kontrollér adressen)", what));
	}
	return {body, response};
}

// Kun almindelig JSON/GeoJSON - ikke JSON-FG (application/vnd.ogc.fg+json: geometrien ligger i "place", ikke "geometry"), som fx ldproxy
// også lister som items-/next-link.
const PLAIN_JSON = /^application\/(geo\+)?json\s*(;|$)/i;

function linkOf(links, rel, typePattern){
	return (links || []).find(l => l && l.rel === rel && l.href && (!typePattern || !l.type || typePattern.test(l.type)));
}

// ---- lag (samlinger) --------------------------------------------------------------------------------------------------------

// Liste over tjenestens lag: [{name (id), title, description, crs: [...], storageCrs, itemsUrl}], kun feature-lag (ikke fx
// kort-/coverage-samlinger). Kan /collections ikke hentes, forsøges landingssidens "data"-link.
export async function fetchOgcCollections(serviceUrl, credentials){
	let {baseUrl, collectionId} = normalizeOgcServiceUrl(serviceUrl);

	let doc;
	let docUrl = buildUrl(baseUrl, "/collections", {});
	try{
		doc = (await getJson(docUrl, credentials, t("Hentning af lag"))).body;
	}catch(firstError){
		let landing;
		try{
			landing = (await getJson(baseUrl, credentials, t("Hentning af landingsside"))).body;
		}catch(e){
			throw firstError;
		}
		let data = linkOf(landing.links, "data", PLAIN_JSON);
		if(!data) throw firstError;
		docUrl = withServiceParams(data.href, baseUrl, baseUrl);
		doc = (await getJson(docUrl, credentials, t("Hentning af lag"))).body;
	}

	if(!doc || !Array.isArray(doc.collections)){
		throw new Error(t("Svaret er ikke en OGC API - Features-lagliste (mangler 'collections') - kontrollér adressen."));
	}

	let globalCrs = Array.isArray(doc.crs) ? doc.crs : [];
	let collections = [];
	for(let c of doc.collections){
		if(!c || !c.id) continue;
		if(c.itemType && c.itemType !== "feature") continue;

		let crs = Array.isArray(c.crs) ? c.crs.flatMap(x => x === "#/crs" ? globalCrs : [x]) : globalCrs;
		// Items-adressen er altid {service}/collections/{id}/items (standarden fastlaegger stien). Samlingens egne items-links bruges
		// ikke: de er pr. format, og fx ldproxy lister JSON-FG ("place" i stedet for "geometry") som type geo+json.
		collections.push({
			name: c.id,
			title: c.title || c.id,
			description: c.description || "",
			crs,
			storageCrs: c.storageCrs || null,
			itemsUrl: buildUrl(baseUrl, `/collections/${encodeURIComponent(c.id)}/items`, {}),
			serviceUrl: baseUrl,
		});
	}

	return {collections, baseUrl, preselect: collectionId};
}

// ---- objekter ---------------------------------------------------------------------------------------------------------------

// Henter ét lags objekter inden for en bbox (følger sidelinks) og returnerer en GeoJSON FeatureCollection.
//   collection: et element fra fetchOgcCollections
//   bbox:       {minX, minY, maxX, maxY} i punktskyens system (targetEpsg)
//   options:    {targetEpsg, bboxLonLat: {minX, minY, maxX, maxY} (lon/lat - bruges når samlingen ikke kan leveres i targetEpsg),
//                credentials, limit, maxFeatures, maxPages, onProgress({features, matched})}
// Svar: {geojson, crsKind: "target" | "lonlat", truncated, numberMatched}
export async function fetchOgcCollectionItems(collection, bbox, options = {}){
	let {targetEpsg, bboxLonLat, credentials, onProgress} = options;
	let limit = options.limit || DEFAULT_PAGE_LIMIT;
	let maxFeatures = options.maxFeatures || DEFAULT_MAX_FEATURES;
	let maxPages = options.maxPages || DEFAULT_MAX_PAGES;
	let what = t("Hentning af \"{0}\"", collection.title || collection.name);

	let pick = pickOgcRequestCrs(collection, targetEpsg);
	let box;
	let params = {limit};
	if(pick.kind === "target"){
		box = bbox;
		params["crs"] = pick.crs;
		params["bbox-crs"] = pick.crs;
	}else{
		box = bboxLonLat;
		if(!box) throw new Error(t("{0}: laget kan ikke leveres i punktskyens koordinatsystem, og en bbox i lon/lat mangler til at hente det i standardsystemet", what));
	}
	params["bbox"] = `${box.minX},${box.minY},${box.maxX},${box.maxY}`;

	let serviceUrl = collection.serviceUrl || collection.itemsUrl;
	let url = buildUrl(collection.itemsUrl, "", params);
	let visited = new Set();
	let features = [];
	let numberMatched = null;
	let truncated = false;
	let pages = 0;

	while(url){
		visited.add(url);
		let {body, response} = await getJson(url, credentials, what, ACCEPT_GEOJSON);
		if(!body || !Array.isArray(body.features)){
			throw new Error(t("{0}: svaret er ikke en GeoJSON FeatureCollection", what));
		}

		if(pages === 0){
			if(Number.isFinite(body.numberMatched)) numberMatched = body.numberMatched;
			// Content-Crs kan kun læses på tværs af domæner, hvis serveren udstiller headeren - ellers udelades kontrollen.
			let contentCrs = response.headers.get("content-crs");
			if(pick.kind === "target" && contentCrs && ogcCrsKey(contentCrs) && ogcCrsKey(contentCrs) !== ogcCrsKey(pick.crs)){
				throw new Error(t("{0}: serveren svarede i et andet koordinatsystem ({1}) end det, der blev bedt om ({2})", what, contentCrs, pick.crs));
			}
		}

		for(let f of body.features){
			if(features.length >= maxFeatures){
				truncated = true;
				break;
			}
			features.push(f);
		}
		pages++;

		let next = linkOf(body.links, "next", PLAIN_JSON);
		let nextUrl = next ? withServiceParams(next.href, url, serviceUrl) : null;
		if(!nextUrl || visited.has(nextUrl)) break;

		if(features.length >= maxFeatures || pages >= maxPages){
			truncated = true;
			break;
		}
		url = nextUrl;
		if(onProgress) onProgress({features: features.length, matched: numberMatched});
	}

	return {geojson: {type: "FeatureCollection", features}, crsKind: pick.kind, truncated, numberMatched};
}
